import { useState, useMemo, useEffect, useRef } from 'react';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { UseColumnManagementProps, ColumnManagementReturn } from '../types';
import { chemicalRestrictedColumns } from '../../../constants';

export function useColumnManagement({
  view,
  tab,
  isEvidence,
  isMapping,
  mappingContent,
  restrictChemical,
}: UseColumnManagementProps): ColumnManagementReturn {
  const hasLoadedFromSavedRef = useRef(false);

  const selectColumnVisibilityModel = useMemo(() => {
    const fnColumnVisibilityModel =
      isMapping && mappingContent?.isActive
        ? () => {
            const model = ColumnVisibilityModels.getByMappingName(
              mappingContent.columVisibilityModelKey,
              isEvidence,
            );
            return model;
          }
        : () => {
            return ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);
          };

    const fnColumnVisibilityModelWithRestrictions = () => {
      const originalList = fnColumnVisibilityModel();
      if (!originalList) return undefined;

      const restrictedList = restrictChemical
        ? chemicalRestrictedColumns
        : [];

      if (restrictedList.length === 0) {
        return originalList;
      }

      return Object.fromEntries(
        Object.entries(originalList).filter(([key]) => !restrictedList.includes(key)),
      );
    };

    return fnColumnVisibilityModelWithRestrictions;
  }, [view, tab, mappingContent?.columVisibilityModelKey, restrictChemical, isMapping, isEvidence]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return selectColumnVisibilityModel();
  });

  const [columnOrder, setColumnOrder] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    const newBaseModel = selectColumnVisibilityModel();
    setColumnVisibilityModel(newBaseModel);

    if (newBaseModel) {
      const orderedKeys = Object.keys(newBaseModel);
      const visibleKeys = orderedKeys.filter(key => newBaseModel[key] === true);
      setColumnOrder(visibleKeys);
    } else {
      setColumnOrder(undefined);
    }

    hasLoadedFromSavedRef.current = false;
  }, [
    selectColumnVisibilityModel,
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent?.isActive,
    mappingContent?.columVisibilityModelKey,
  ]);

  const resetToDefault = () => {
    const baseDefaultModel =
      isMapping && mappingContent?.isActive
        ? ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictedList = restrictChemical
      ? chemicalRestrictedColumns
      : [];

    const defaultModel = baseDefaultModel && restrictedList.length > 0
      ? Object.fromEntries(Object.entries(baseDefaultModel).filter(([key]) => !restrictedList.includes(key)))
      : baseDefaultModel;

    if (defaultModel) {
      setColumnVisibilityModel(defaultModel);
      const defaultColumnOrder = Object.keys(defaultModel).filter(key => defaultModel[key] === true);
      setColumnOrder(defaultColumnOrder);
    }

    hasLoadedFromSavedRef.current = false;
  };

  return {
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnOrder,
    setColumnOrder,
    hasLoadedFromSavedRef,
    resetToDefault,
  };
}
